import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  Injector,
  viewChild,
  viewChildren
} from '@angular/core';
import { PageFilterListService } from '../../../services/page-filter-list.service';
import { PageFilterListItemComponent } from './page-filter-item/page-filter-list-item.component';
import { PageFilterService } from '../../../services/page-filter.service';
import { PageFilterItem } from '../../../types/page-filter-item';
import { PageFilterDetailComponent } from '../page-filter-detail/page-filter-detail.component';
import { PageFilterComponentBase } from '../page-filter-component-base';
import { ButtonComponent, SearchInputComponent, TranslatePipe, translator } from '@softline/ui-core';
import { isSimpleFilter } from '../../../../helper/is-simple-filter.function';

@Component({
  selector: 'soft-page-filter-list',
  imports: [
    PageFilterListItemComponent,
    PageFilterDetailComponent,
    SearchInputComponent,
    ButtonComponent,
    TranslatePipe
  ],
  templateUrl: './page-filter-list.component.html',
  styleUrl: './page-filter-list.component.css',
  providers: [
    PageFilterListService,
    {provide: PageFilterComponentBase, useExisting: forwardRef(() => PageFilterListComponent)}
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageFilterListComponent extends PageFilterComponentBase {
  private readonly t = translator();

  readonly service = inject(PageFilterService);
  readonly listService = inject(PageFilterListService);
  readonly detail = viewChild(PageFilterDetailComponent);
  private readonly rows = viewChildren(PageFilterListItemComponent);
  private readonly injector = inject(Injector);

  // a selection whose source holds a custom tree has no detail view - fall back to the list
  readonly selectedItem = computed(() => {
    const item = this.service.selectedItem();
    return item && !this.isItemDisabled(item) ? item : null;
  });

  // sources whose draft tree the list tab cannot represent (nested/custom conditions)
  readonly hasComplexState = computed(() =>
    this.service.states().some(state => !isSimpleFilter(state.state()))
  );

  constructor() {
    super();
    // a chip asked for its row: make it visible, then focus after the modal's own
    // first-input focus (queueMicrotask) has run - hence render + animation frame
    effect(() => {
      const item = this.service.focusedItem();
      if(!item)
        return;
      if(this.isItemDisabled(item)) {
        this.service.focusedItem.set(null);
        return;
      }
      this.listService.search.set('');
      afterNextRender(() => requestAnimationFrame(() => {
        this.rows().find(row => row.item() === item)?.focus();
        this.service.focusedItem.set(null);
      }), { injector: this.injector });
    });
  }

  override title = computed(() => {
    const selectedItem = this.selectedItem();
    if(selectedItem)
      return selectedItem.config.title();
    return this.t('#CORE_FILTER.TITLE')()
  });

  override canBack = computed(() => this.selectedItem() !== null);
  override canApply = computed(() => true);
  override tabsVisible = computed(() => this.selectedItem() === null)

  isItemDisabled(item: PageFilterItem<any, any>): boolean {
    const state = this.service.states().find(o => o.config === item.source.state.config);
    return !!state && !isSimpleFilter(state.state());
  }

  onItemClick(item: PageFilterItem<any, any>) {
    if(this.isItemDisabled(item))
      return;
    // toggle items have no detail view - the row flips the fixed filter in the draft
    if(item.toggle) {
      this.service.toggleItemFilter(item, 'draft');
      return;
    }
    this.service.selectedItem.set(item);
  }

  isToggleActive(item: PageFilterItem<any, any>): boolean {
    return !!item.toggle && this.service.isToggleItemActive(item, 'draft');
  }

  onClearFilter(item: PageFilterItem<any, any>): void {
    if(this.isItemDisabled(item))
      return;
    this.service.applyItemFilter(item, null, 'draft');
  }

  switchToCustomTab(): void {
    this.service.tab.set('custom');
  }

  override back = () => {
    this.service.selectedItem.set(null);
  }

  override apply = () => {
    const filterInput = this.detail()?.getFilterInput() ?? null;
    const item = this.selectedItem();
    if(filterInput && item) {
      const filter = filterInput.getFilterValue();
      this.service.applyItemFilter(item, filter, 'draft');
      this.service.selectedItem.set(null);
      return;
    }
    this.service.applyFilter();
    this.service.opened.set(false);
  }
}
